import { z } from 'zod'
import { asEnumType, asObjectType } from '@gqloom/zod'

import {
  createVersionedFileConfigFieldSchema,
  FileSupportedVariant,
  fileVersionSchema,
} from '../file-upload/file-upload.schemas'

export const ImageSupportedVariant = {
  ...FileSupportedVariant,
  Thumbnail: 'thumbnail',
  Small: 'small',
  Medium: 'medium',
  Large: 'large',
} as const

export const NativeImageVariant = {
  NativeAppIcon: 'nativeAppIcon',
  GooglePlayAppIcon: 'googlePlayAppIcon',
  NativeSplash: 'nativeSplash',
} as const

export const StoredImageVariant = {
  ...ImageSupportedVariant,
  ...NativeImageVariant,
} as const

export const ImageSupportedContentType = {
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  png: 'image/png',
} as const

export const ImageSupportedContentTypeAlias = {
  jpeg: ['jpg'],
} as const satisfies Partial<Record<keyof typeof ImageSupportedContentType, string[]>>

export const IMAGE_SUPPORTED_CONTENT_TYPE_ALIAS = Object.values(
  ImageSupportedContentTypeAlias
).flat()

export const IMAGE_SUPPORTED_VARIANTS = Object.values(
  ImageSupportedVariant
) as (typeof ImageSupportedVariant)[keyof typeof ImageSupportedVariant][]

export const IMAGE_SUPPORTED_TYPES = Object.keys(
  ImageSupportedContentType
) as (keyof typeof ImageSupportedContentType)[]

const imageSupportedFormatEnumSchema = z.enum(IMAGE_SUPPORTED_TYPES).register(asEnumType, {
  name: 'ImageFileVersionFormat',
})

export const imageFileVersionSchema = fileVersionSchema
  .extend({
    type: z.enum(StoredImageVariant).register(asEnumType, {
      name: 'ImageFileVersionType',
    }),
    width: z.int().nullable(),
    height: z.int().nullable(),
    format: imageSupportedFormatEnumSchema,
    byteSize: z.number().int().positive().optional(),
    sha256: z
      .string()
      .regex(/^[a-f0-9]{64}$/)
      .optional(),
  })
  .register(asObjectType, {
    name: 'ImageFileVersion',
    extensions: {
      directives: {
        shareable: true, // Can be resolved by multiple subgraphs
      },
    },
  })

export const imageFileConfigFieldSchema = createVersionedFileConfigFieldSchema(
  imageFileVersionSchema
).register(asObjectType, {
  name: 'ImageFileConfigField',
  extensions: {
    directives: {
      shareable: true,
    },
  },
})

export const imagePublicSignedUrlSchema = imageFileVersionSchema
  .omit({
    format: true,
  })
  .register(asObjectType, {
    name: 'ImagePublicSignedUrl',
    extensions: {
      directives: {
        shareable: true,
      },
    },
  })

export const imagePublicSignedUrlInputSchema = z
  .object({
    format: imageSupportedFormatEnumSchema,
    type: z.enum(IMAGE_SUPPORTED_VARIANTS).register(asEnumType, {
      name: 'ImageSupportedVariant',
      description: 'All supported variants of an image',
    }),
  })
  .register(asObjectType, {
    name: 'ImagePublicSignedUrlInput',
  })

export const generateImagePublicSignedUrlInputSchema = imagePublicSignedUrlInputSchema
  .pick({
    format: true,
  })
  .register(asObjectType, {
    name: 'GenerateImagePublicSignedUrlInput',
  })

// Schema for generateFileSignedUrl response that works for both files and images
// Makes image-specific fields optional so it can handle both types
export const generateFileSignedUrlResponseSchema = fileVersionSchema
  .extend({
    type: z.enum(StoredImageVariant).register(asEnumType, {
      name: 'ImageFileVersionType',
    }),
    width: z.int().optional(),
    height: z.int().optional(),
    format: imageSupportedFormatEnumSchema.optional(),
  })
  .register(asObjectType, {
    name: 'GenerateFileSignedUrlResponse',
    extensions: {
      directives: {
        shareable: true,
      },
    },
  })

export type ImageFormat = keyof typeof ImageSupportedContentType
export type ImageFormatContentType = (typeof ImageSupportedContentType)[ImageFormat]
export type ImageVariantName = (typeof IMAGE_SUPPORTED_VARIANTS)[number]

export type ImageFileVersionSchema = z.infer<typeof imageFileVersionSchema>
export type ImageFileStateColumnSchema = z.infer<typeof imageFileConfigFieldSchema>
export type ImagePublicSignedUrlSchema = z.infer<typeof imagePublicSignedUrlSchema>
export type ImagePublicSignedUrlInputSchema = z.infer<typeof imagePublicSignedUrlInputSchema>
